import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import React, { useState } from 'react';

import client from '@/lib/feathers';
import useService from '@/lib/hooks/useService';
import type CommunityGroups from '@/types/database/communityGroups';
import type Points from '@/types/database/points';
import type { SubscriptionPlan } from '@/types/database/subscriptionPlan';
import type { Uploads } from '@/types/database/uploads';
import type UserGroupRoute from '@/types/database/userGroupRoute';

import {
  addAndEditPlanModalBodyStyles,
  checkPlanNameIsExist,
  customRequest,
  expirationOptions,
  getFileData,
  getFileListFromEvent,
  getServiceId,
} from '../helper';
import type { PlanFormValues } from '../types';
import styles from './index.module.scss';

const { Item, useForm } = Form;
const { TextArea } = Input;
const { Text } = Typography;

interface AddAndEditPlanModalWithButtonProps {
  type: 'add' | 'edit';
  updateSubscriptionPlans: () => void;
  updateUploads: () => void;
  userGroupRoutes: UserGroupRoute[];
  record?: SubscriptionPlan;
  uploads?: Uploads[];
}

const AddOrEditModalWithButton = ({
  type,
  updateSubscriptionPlans,
  record,
  userGroupRoutes,
}: AddAndEditPlanModalWithButtonProps) => {
  const [form] = useForm<PlanFormValues>();

  const uploadsService = client.service('uploads');
  const [subscriptionPlans, , subscriptionPlanService] =
    useService<SubscriptionPlan>({
      collectionName: 'subscriptionPlan',
    });

  const [communityGroups] = useService<CommunityGroups>({
    collectionName: 'communityGroups',
    query: {
      householdNumber: { $exists: false },
    },
  });
  const [points] = useService<Points>({ collectionName: 'points' });

  const [isOpenPlanModal, setIsOpenPlanModal] = useState(false);
  const [pointId, setPointId] = useState('');

  const removeFile = async (file: UploadFile<Uploads>) => {
    const fileId = file.response?._id;
    const query = { query: { showPath: file.url } };

    try {
      fileId
        ? await uploadsService.remove(fileId)
        : await uploadsService.remove(null, query);
      message.success('圖片移除成功!');
    } catch (error) {
      message.error('圖片移除失敗!');
    }
  };

  const closeModal = () => {
    if (type === 'add') {
      form.resetFields();
    }
    setIsOpenPlanModal(false);
  };

  const updatePlan = async (
    data: Omit<SubscriptionPlan, '_id' | 'createdTime' | 'updatedTime'> & {
      _id?: string;
      updatedTime?: number;
    }
  ) => {
    try {
      if (type === 'add') {
        await subscriptionPlanService.create(data);
      }
      if (type === 'edit') {
        await subscriptionPlanService.patch(record?._id, data);
      }
      updateSubscriptionPlans();
      closeModal();
      message.success(type === 'add' ? '新增方案成功!' : '編輯方案成功!');
    } catch (error) {
      message.error(type === 'add' ? '新增方案失敗!' : '編輯方案失敗!');
    }
  };

  const handlePlanFormSubmit = async (
    formValues: PlanFormValues,
    formType: 'add' | 'edit'
  ) => {
    const {
      payByCreditCard,
      payByPoint,
      pointQuantities,
      creditCardPrice,
      serviceName,
      file,
      ...otherValues
    } = formValues;

    const serviceId = getServiceId(userGroupRoutes, serviceName) as string;

    const fileData = getFileData(file);
    const isFileDataArray = Array.isArray(fileData);

    let fileUrl = isFileDataArray
      ? fileData[0]?.response?.showPath
      : fileData?.showPath;

    if (
      isFileDataArray &&
      fileData.length !== 0 &&
      fileData[0].url === fileData[0].uid
    ) {
      fileUrl = fileUrl ? fileUrl : fileData && fileData[0].url;
    }

    const data = {
      ...otherValues,
      communityName: formValues.communityName ?? null,
      isPlanActive: true,
      creditCardPrice: payByCreditCard ? creditCardPrice : null,
      points: {
        id: payByPoint ? pointId : null,
        quantity: payByPoint ? pointQuantities : null,
      },
      serviceId,
      file: fileUrl ?? null,
    };

    if (formType === 'edit' && record) {
      data.isPlanActive = record.isPlanActive;
      data.updatedTime = dayjs().valueOf();
    }

    await updatePlan(data);
  };

  const communityNameOptions = communityGroups.map((community) => ({
    label: community.name,
    value: community.name,
  }));

  const creditCardPaymentValidator = async () => {
    const isPayByCreditCardChecked: PlanFormValues['payByCreditCard'] =
      form.getFieldValue('payByCreditCard');
    const isPayByPointChecked: PlanFormValues['payByPoint'] =
      form.getFieldValue('payByPoint');
    const creditCardPrice: PlanFormValues['creditCardPrice'] =
      form.getFieldValue('creditCardPrice');

    if (!isPayByPointChecked && !isPayByCreditCardChecked) {
      return await Promise.reject(new Error('請至少選擇一種'));
    }
    if (!isPayByCreditCardChecked && creditCardPrice) {
      return await Promise.reject(new Error('請記得勾選選項'));
    }

    return await Promise.resolve();
  };

  const savePointId = async (pointId: string) => {
    setPointId(pointId);
    await Promise.resolve();
  };

  const pointPaymentValidator = async () => {
    const isPayByCreditCardChecked: PlanFormValues['payByCreditCard'] =
      form.getFieldValue('payByCreditCard');
    const isPayByPointChecked: PlanFormValues['payByPoint'] =
      form.getFieldValue('payByPoint');
    const pointQuantities: PlanFormValues['pointQuantities'] =
      form.getFieldValue('pointQuantities');

    if (!isPayByPointChecked && !isPayByCreditCardChecked) {
      return await Promise.reject(new Error('請至少選擇一種'));
    }
    if (!isPayByPointChecked && pointQuantities) {
      return await Promise.reject(new Error('請記得勾選選項'));
    }

    const serviceName = form.getFieldValue('serviceName');

    if (serviceName && isPayByPointChecked) {
      const serviceName = form.getFieldValue('serviceName');
      const serviceId = getServiceId(userGroupRoutes, serviceName);
      const foundPointData = points.find(
        (point) => point.serviceId === serviceId
      );

      return foundPointData
        ? await savePointId(foundPointData._id)
        : await Promise.reject('該服務無法使用點數功能');
    }

    return await Promise.resolve();
  };

  const handleButtonClick = () => {
    if (type === 'edit' && record) {
      const { file } = record;

      form.setFieldsValue({
        ...record,
        file:
          file !== null
            ? [
                {
                  name: file.split('/')[file.split('/').length - 1],
                  url: file,
                  thumbUrl: file,
                  uid: file,
                },
              ]
            : null,
      });
    }
    setIsOpenPlanModal(true);
  };

  const getServiceOptions = () => {
    const serviceOptions = userGroupRoutes.reduce(
      (options: { label: string; value: string }[], userGroupRoute) => {
        if (userGroupRoute.isServiceActive) {
          const option = {
            label: userGroupRoute.name,
            value: userGroupRoute.name,
          };
          options.push(option);
        }
        return options;
      },
      []
    );

    return serviceOptions;
  };

  const serviceOptions = getServiceOptions();

  return (
    <>
      <Button onClick={handleButtonClick}>
        {type === 'add' ? '新增方案' : '編輯'}
      </Button>
      <Modal
        title={type === 'add' ? '新增方案' : '編輯方案'}
        open={isOpenPlanModal}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
        centered
        bodyStyle={addAndEditPlanModalBodyStyles}
      >
        <Form
          form={form}
          preserve={false}
          onFinish={(formValues) => handlePlanFormSubmit(formValues, type)}
          layout="vertical"
          initialValues={{
            description: '',
            rules: '',
          }}
        >
          <Item
            label={<Text type="secondary">計費方案名稱</Text>}
            name="planName"
            rules={[
              {
                required: true,
                validator: (_rules, value) =>
                  checkPlanNameIsExist({
                    subscriptionPlans,
                    planName: value,
                    formType: type,
                  }),
              },
            ]}
          >
            <Input
              placeholder="請輸入計費方案名稱"
              className={styles.widthFull}
            />
          </Item>
          <Item
            label={<Text type="secondary">服務</Text>}
            name="serviceName"
            rules={[{ required: true, message: '請選擇服務' }]}
          >
            <Select placeholder="請選擇服務" options={serviceOptions} />
          </Item>
          <Item
            label={<Text type="secondary">方案用戶</Text>}
            name="communityName"
          >
            <Select
              placeholder="請選擇方案用戶"
              options={communityNameOptions}
              allowClear
              disabled={type === 'edit'}
            />
          </Item>
          <Item
            label={<Text type="secondary">效期</Text>}
            name="expiration"
            rules={[{ required: true, message: '請選擇效期' }]}
          >
            <Select placeholder="請選擇效期" options={expirationOptions} />
          </Item>
          <Item label={<Text type="secondary">付款方式</Text>} required>
            <Row>
              <Col span={6}>
                <Item
                  name="payByCreditCard"
                  valuePropName="checked"
                  rules={[
                    {
                      required: true,
                      validator: creditCardPaymentValidator,
                    },
                  ]}
                  initialValue={type === 'edit' && record?.creditCardPrice}
                >
                  <Checkbox>台灣Pay</Checkbox>
                </Item>
              </Col>
              <Col span={12} className={styles.priceInputContainer}>
                <span className={styles.addMarginBottom}>金額：</span>
                <Item
                  name="creditCardPrice"
                  rules={[
                    {
                      required: true,
                      validator: async () => {
                        const isPayByCreditCardChecked: PlanFormValues['payByCreditCard'] =
                          form.getFieldValue('payByCreditCard');
                        const creditCardPrice: PlanFormValues['creditCardPrice'] =
                          form.getFieldValue('creditCardPrice');
                        if (isPayByCreditCardChecked && !creditCardPrice) {
                          return await Promise.reject(new Error('請輸入金額'));
                        }
                        return await Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="請輸入金額"
                    min={0}
                    className={styles.widthFull}
                  />
                </Item>
              </Col>
            </Row>
            <Row>
              <Col span={6}>
                <Item
                  name="payByPoint"
                  valuePropName="checked"
                  rules={[
                    {
                      required: true,
                      validator: pointPaymentValidator,
                    },
                  ]}
                  initialValue={type === 'edit' && record?.points.quantity}
                >
                  <Checkbox>點數</Checkbox>
                </Item>
              </Col>
              <Col span={12} className={styles.priceInputContainer}>
                <span className={styles.addMarginBottom}>金額：</span>
                <Item
                  name="pointQuantities"
                  rules={[
                    {
                      required: true,
                      validator: async () => {
                        const isPayByPointChecked: PlanFormValues['payByPoint'] =
                          form.getFieldValue('payByPoint');
                        const pointQuantities: PlanFormValues['pointQuantities'] =
                          form.getFieldValue('pointQuantities');
                        if (isPayByPointChecked && !pointQuantities) {
                          return await Promise.reject(new Error('請輸入金額'));
                        }
                        return await Promise.resolve();
                      },
                    },
                  ]}
                  initialValue={type === 'edit' && record?.points.quantity}
                >
                  <InputNumber
                    placeholder="請輸入金額"
                    min={0}
                    className={styles.widthFull}
                  />
                </Item>
              </Col>
            </Row>
          </Item>
          <Item
            label={<Text type="secondary">方案介紹</Text>}
            name="description"
          >
            <TextArea
              placeholder="請輸入方案介紹"
              maxLength={100}
              rows={4}
              showCount
            />
          </Item>
          <Item label={<Text type="secondary">方案規則</Text>} name="rules">
            <TextArea
              placeholder="請輸入方案規則"
              maxLength={100}
              rows={4}
              showCount
            />
          </Item>
          <Item
            label={<Text type="secondary">上傳圖片</Text>}
            name="file"
            valuePropName="fileList"
            getValueFromEvent={getFileListFromEvent}
          >
            <Upload
              accept="image/*"
              listType="picture-card"
              maxCount={1}
              customRequest={customRequest}
              onRemove={removeFile}
            >
              <div>
                <PlusOutlined />
                <div className={styles.uploadButton}>上傳圖片</div>
              </div>
            </Upload>
          </Item>
          <Item>
            <Space className={styles.footerButtonContainer}>
              <Button onClick={closeModal}>取消</Button>
              <Button htmlType="submit" type="primary">
                確定
              </Button>
            </Space>
          </Item>
        </Form>
      </Modal>
    </>
  );
};

export default AddOrEditModalWithButton;
